import { apiHelpers } from './api';

// ============================================
// TYPES
// ============================================

export interface SpotifyConnectionStatus {
  connected: boolean;
  spotifyUserId?: string;
  displayName?: string;
  email?: string;
}

export interface SpotifyAuthResponse {
  authUrl: string;
}

export interface SpotifyTrack {
  id: string;
  uri: string;
  name: string;
  artist: string;
  album: string;
  durationMs: number;
  imageUrl: string | null;
  previewUrl?: string | null;
}

export interface SpotifySearchResult {
  tracks: SpotifyTrack[];
  total: number;
  limit: number;
  offset: number;
}

export interface EventPlaylist {
  id: string;
  spotifyPlaylistId: string;
  spotifyUrl: string;
  name: string;
  description: string | null;
  trackCount: number;
  isCollaborative: boolean;
  tracks: PlaylistTrack[];
}

export interface PlaylistTrack {
  id: string;
  name: string;
  artist: string;
  album: string | null;
  durationMs: number;
  imageUrl: string | null;
  addedAt: string;
}

export interface CreatePlaylistInput {
  name: string;
  description?: string;
  isCollaborative?: boolean;
}

export interface AddTracksResult {
  addedCount: number;
  snapshotId: string;
}

export interface RemoveTracksResult {
  removedCount: number;
  snapshotId: string;
}

// ============================================
// SERVICE
// ============================================

export const spotifyService = {
  // ============================================
  // OAUTH
  // ============================================

  /**
   * Get authorization URL to connect Spotify
   */
  async getAuthUrl(redirectUrl?: string): Promise<string> {
    const params = redirectUrl ? `?redirectUrl=${encodeURIComponent(redirectUrl)}` : '';
    const response = await apiHelpers.get<SpotifyAuthResponse>(`/spotify/auth${params}`);
    return response.authUrl;
  },

  /**
   * Get Spotify connection status
   */
  async getConnectionStatus(): Promise<SpotifyConnectionStatus> {
    return apiHelpers.get<SpotifyConnectionStatus>('/spotify/status');
  },

  /**
   * Disconnect Spotify account
   */
  async disconnect(): Promise<void> {
    await apiHelpers.delete('/spotify/disconnect');
  },

  // ============================================
  // SEARCH
  // ============================================

  /**
   * Search for tracks on Spotify
   */
  async searchTracks(
    query: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<SpotifySearchResult> {
    const params = new URLSearchParams({
      query,
      limit: limit.toString(),
      offset: offset.toString(),
    });
    return apiHelpers.get<SpotifySearchResult>(`/spotify/search?${params.toString()}`);
  },

  // ============================================
  // PLAYLISTS
  // ============================================

  /**
   * Get playlist for an event
   */
  async getEventPlaylist(eventId: string): Promise<EventPlaylist | null> {
    try {
      return await apiHelpers.get<EventPlaylist>(`/spotify/events/${eventId}/playlist`);
    } catch (error) {
      // Return null if no playlist exists
      if ((error as { status?: number }).status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Create playlist for an event
   */
  async createPlaylist(eventId: string, data: CreatePlaylistInput): Promise<EventPlaylist> {
    return apiHelpers.post<EventPlaylist>(`/spotify/events/${eventId}/playlist`, data);
  },

  /**
   * Add tracks to event playlist
   */
  async addTracks(
    eventId: string,
    trackUris: string[],
    position?: number
  ): Promise<AddTracksResult> {
    return apiHelpers.post<AddTracksResult>(`/spotify/events/${eventId}/playlist/tracks`, {
      trackUris,
      position,
    });
  },

  /**
   * Remove tracks from event playlist
   */
  async removeTracks(eventId: string, trackUris: string[]): Promise<RemoveTracksResult> {
    return apiHelpers.delete<RemoveTracksResult>(`/spotify/events/${eventId}/playlist/tracks`, {
      trackUris,
    });
  },

  /**
   * Sync playlist with Spotify
   */
  async syncPlaylist(eventId: string): Promise<void> {
    await apiHelpers.post(`/spotify/events/${eventId}/playlist/sync`);
  },
};

export default spotifyService;
